/**
 * Pesajes (Req. 15; tarea 16.2): peso, unidad, báscula opcional, fecha, hora
 * y nota. Se guarda el peso en kg con precisión completa y lo que se escribió
 * (Req. 5.4); la tendencia no se guarda, sale de aquí (domain/tendencia.ts).
 */

import { db, leerSettings } from './schema';
import { enEscritura } from './tx';
import type { Scale, Settings, WeighIn } from './types';
import { ahoraIso, esFechaLocal, type FechaLocal } from '@/domain/dates';
import { ErrorValidacion } from '@/domain/errors';
import { esHoraLocal, instanteLocal, type HoraLocal } from '@/domain/time';
import { aKg, esUnidadPeso, type UnidadPeso } from '@/domain/units';
import { PESO_MAX_KG, PESO_MIN_KG, pesajeLegible, rangoDePeso, resumenDePeso, type ResumenDePeso } from '@/domain/tendencia';

export const NOTA_PESAJE_MAX = 200;

/**
 * Los pesajes que se pueden leer, del más reciente al más antiguo por su
 * momento. Los que traen la fecha, la hora o el peso dañados (de un respaldo
 * editado a mano) no se muestran ni cuentan: siguen guardados tal cual y se
 * cuentan aparte (`contarPesajesIlegibles`). LECTURA PURA.
 */
export async function leerPesajes(): Promise<WeighIn[]> {
  const pesajes = (await db.weighIns.toArray()).filter(pesajeLegible);
  return pesajes.sort((a, b) => Date.parse(b.momento) - Date.parse(a.momento) || String(b.creadoEn).localeCompare(String(a.creadoEn)));
}

/** Cuántos pesajes guardados no se pueden leer (revisión de la 0.4.0). LECTURA PURA. */
export async function contarPesajesIlegibles(): Promise<number> {
  return db.weighIns.filter((p) => !pesajeLegible(p)).count();
}

/** LECTURA PURA. */
export async function leerPesaje(id: string): Promise<WeighIn | undefined> {
  return db.weighIns.get(id);
}

export interface PesoDeHoy {
  resumen: ResumenDePeso<WeighIn>;
  /** La báscula que cuenta para la tendencia; null = todas. */
  basculaDeTendencia: Scale | null;
  /** La báscula del último pesaje, si tiene. */
  basculaDelUltimo: Scale | null;
}

/** El último pesaje, la tendencia de hoy y su cambio, para Hoy y Cuerpo (Req. 16.3, 24.1). LECTURA PURA. */
export async function leerPesoDeHoy(hoy: FechaLocal): Promise<PesoDeHoy> {
  const [pesajes, ajustes, basculas] = await Promise.all([db.weighIns.toArray(), leerSettings(), db.scales.toArray()]);
  const resumen = resumenDePeso(pesajes.filter(pesajeLegible), ajustes.basculaTendenciaId, hoy);
  const deId = (id: string | null) => (id === null ? null : (basculas.find((b) => b.id === id) ?? null));
  return {
    resumen,
    basculaDeTendencia: deId(ajustes.basculaTendenciaId),
    basculaDelUltimo: deId(resumen.ultimo?.scaleId ?? null),
  };
}

export interface PesajeACapturar {
  /** Lo crea la pantalla al abrirse (nuevo) o es el del pesaje que se edita. */
  id: string;
  fecha: FechaLocal;
  hora: HoraLocal;
  /** Tal como se escribió (Req. 5.4). */
  valor: number;
  unidad: UnidadPeso;
  scaleId: string | null;
  nota: string;
}

/**
 * Guarda un pesaje nuevo o los cambios a uno (Req. 15.1, 15.5). Valida el
 * rango (Req. 15.6) y que no quede en un día futuro. La pregunta de unidad
 * (Req. 15.4) la hace la pantalla antes de llamar aquí: esto guarda lo que se
 * le pasa, tal como se escribió. Uno nuevo deja su báscula como la última
 * usada (Req. 14.3). Con el id de la pantalla, dos toques no duplican. ESCRIBE.
 */
export async function guardarPesaje(p: PesajeACapturar, hoy: FechaLocal): Promise<WeighIn> {
  if (!esFechaLocal(p.fecha)) throw new ErrorValidacion('La fecha no es válida.', 'fecha');
  if (p.fecha > hoy) throw new ErrorValidacion('El pesaje no puede quedar en un día que todavía no llega.', 'fecha');
  if (!esHoraLocal(p.hora)) throw new ErrorValidacion('La hora no es válida.', 'hora');
  if (!esUnidadPeso(p.unidad)) throw new ErrorValidacion('Elige kg o lb.', 'peso');
  const pesoKg = aKg(p.valor, p.unidad);
  if (!(p.valor > 0) || !Number.isFinite(pesoKg) || pesoKg < PESO_MIN_KG || pesoKg > PESO_MAX_KG) {
    throw new ErrorValidacion(`El peso debe estar entre ${rangoDePeso(p.unidad)}.`, 'peso');
  }
  const nota = p.nota.trim();
  if (nota.length > NOTA_PESAJE_MAX) {
    throw new ErrorValidacion(`La nota puede tener hasta ${NOTA_PESAJE_MAX} caracteres.`, 'nota');
  }

  return enEscritura([db.weighIns, db.scales, db.settings], async () => {
    const anterior = await db.weighIns.get(p.id);
    if (p.scaleId !== null) {
      const bascula = await db.scales.get(p.scaleId);
      if (bascula === undefined) throw new ErrorValidacion('Esa báscula ya no existe: elige otra.', 'bascula');
      // Una archivada solo se conserva en un pesaje que ya era de ella
      if (bascula.archivada && anterior?.scaleId !== p.scaleId) {
        throw new ErrorValidacion(`"${bascula.nombre}" está archivada: elige otra o desarchívala.`, 'bascula');
      }
    }
    const ahora = ahoraIso();
    const pesaje: WeighIn = {
      id: p.id,
      fecha: p.fecha,
      hora: p.hora,
      momento: instanteLocal(p.fecha, p.hora),
      pesoKg,
      capturado: { valor: p.valor, unidad: p.unidad },
      scaleId: p.scaleId,
      nota: nota === '' ? null : nota,
      creadoEn: anterior?.creadoEn ?? ahora,
      actualizadoEn: ahora,
    };
    await db.weighIns.put(pesaje);
    if (anterior === undefined) {
      const cambios: Partial<Pick<Settings, 'ultimaBasculaId'>> = { ultimaBasculaId: p.scaleId };
      await db.settings.update(1, cambios);
    }
    return pesaje;
  });
}

/** Borra un pesaje (Req. 15.5); la pantalla ya pidió confirmación. ESCRIBE. */
export async function borrarPesaje(id: string): Promise<void> {
  await enEscritura([db.weighIns], async () => {
    await db.weighIns.delete(id);
  });
}
